import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserProfile,
  UserUpdate,
  UserPassword,
  ProfileCompletion,
  getBrandUsers,
  getBrand,
  updateBrand,
  updateUserProfile,
  addUserToBrand,
  reInviteUserToBrand,
  deactivateBrandUser,
  reactivateBrandUser,
  deleteBrandUser,
  updateBrandUserRole,
  getBrandDeposits,
  updateUserYoutubeTokens,
  removeUserYoutubeTokens,
  updateUserInstagramTokens,
  updateUserTiktokTokens,
  removeUserInstaTokens,
  removeUserTiktokTokens,
  deleteUser,
  activateBrandUser,
  whitelistUser,
  fetchLocation,
  login,
  fetchUser,
} from '../../services/userService';
import {
  getUserSubscription,
  cancelSubscriptionAPI,
  resumeSubscriptionAPI,
} from '../../services/linkService';
import { queryKeys } from './queryKeys';

export const useUserProfileQuery = (userId: string) =>
  useQuery({
    queryKey: queryKeys.user.profile(userId),
    queryFn: () => UserProfile(userId),
    enabled: !!userId,
  });

export const useProfileCompletionQuery = (userId: string) =>
  useQuery({
    queryKey: queryKeys.user.profileCompletion(userId),
    queryFn: () => ProfileCompletion(userId),
    enabled: !!userId,
  });

export const useBrandUsersQuery = (
  userId: string,
  brandId: string,
  page: number,
  limit: number,
  searchTerm?: string,
) =>
  useQuery({
    queryKey: queryKeys.user.brandUsers(
      userId,
      brandId,
      page,
      limit,
      searchTerm,
    ),
    queryFn: () => getBrandUsers(userId, brandId, page, limit, searchTerm),
    enabled: !!userId && !!brandId,
  });

export const useBrandQuery = (userId: string, brandId: string) =>
  useQuery({
    queryKey: queryKeys.user.brand(userId, brandId),
    queryFn: () => getBrand(userId, brandId),
    enabled: !!userId && !!brandId,
  });

export const useBrandDepositsQuery = (
  brandId: string,
  page: number,
  limit: number,
) =>
  useQuery({
    queryKey: queryKeys.user.deposits(brandId, page, limit),
    queryFn: () => getBrandDeposits(brandId, page, limit),
    enabled: !!brandId,
  });

export const useUpdateUser = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: any) => UserUpdate(userId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.user.profile(userId),
      });
    },
  });
};

export const useUpdatePassword = (userId: string) =>
  useMutation({
    mutationFn: (values: any) => UserPassword(userId, values),
  });

export const useUpdateBrand = (userId: string, brandId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: any) => updateBrand(userId, brandId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.user.brand(userId, brandId),
      });
    },
  });
};

export const useUpdateUserProfile = () =>
  useMutation({
    mutationFn: (formData: FormData) => updateUserProfile(formData),
  });

export const useAddUserToBrand = (userId: string, brandId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      email,
      role,
    }: {
      name: string;
      email: string;
      role: string;
    }) => addUserToBrand(userId, brandId, name, email, role),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user', 'brandUsers'],
      });
    },
  });
};

export const useReInviteUserToBrand = (userId: string, brandId: string) =>
  useMutation({
    mutationFn: (email: string) => reInviteUserToBrand(userId, brandId, email),
  });

export const useDeactivateBrandUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetUserId: string) => deactivateBrandUser(targetUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user', 'brandUsers'],
      });
    },
  });
};

export const useReactivateBrandUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetUserId: string) => reactivateBrandUser(targetUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user', 'brandUsers'],
      });
    },
  });
};

export const useDeleteBrandUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetUserId: string) => deleteBrandUser(targetUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user', 'brandUsers'],
      });
    },
  });
};

export const useUpdateBrandUserRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      updateBrandUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user', 'brandUsers'],
      });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'users'],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.admin.allUsersAndLinks(),
      });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'allUsersAndLinksPaged'],
      });
    },
  });
};

export const useUpdateYoutubeTokens = () =>
  useMutation({
    mutationFn: (code: string) => updateUserYoutubeTokens(code),
  });

export const useRemoveYoutubeTokens = () =>
  useMutation({
    mutationFn: () => removeUserYoutubeTokens(),
  });

export const useUpdateInstagramTokens = () =>
  useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) =>
      updateUserInstagramTokens(code, state),
  });

export const useUpdateTiktokTokens = () =>
  useMutation({
    mutationFn: ({ code, userId }: { code: string; userId: string }) =>
      updateUserTiktokTokens(code, userId),
  });

export const useRemoveInstaTokens = () =>
  useMutation({
    mutationFn: (userId: string) => removeUserInstaTokens(userId),
  });

export const useRemoveTiktokTokens = () =>
  useMutation({
    mutationFn: (userId: string) => removeUserTiktokTokens(userId),
  });

export const useActivateBrandUser = () =>
  useMutation({
    mutationFn: ({
      token,
      userId,
      password,
    }: {
      token: string;
      userId: string;
      password: string;
    }) => activateBrandUser(token, userId, password),
  });

export const useWhitelistUser = () =>
  useMutation({
    mutationFn: (userId: string) => whitelistUser(userId),
  });

export const useUserSubscriptionQuery = (userId: string) =>
  useQuery({
    queryKey: queryKeys.user.subscription(userId),
    queryFn: () => getUserSubscription(userId),
    enabled: !!userId,
    staleTime: 30_000,
  });

export const useCancelSubscriptionMutation = () =>
  useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      cancelSubscriptionAPI(userId, reason),
  });

export const useResumeSubscriptionMutation = () =>
  useMutation({
    mutationFn: (userId: string) => resumeSubscriptionAPI(userId),
  });

export const useFetchLocationQuery = (keyword: string, areaType?: string) =>
  useQuery({
    queryKey: queryKeys.location.search(keyword, areaType),
    queryFn: () => fetchLocation(keyword, areaType),
    enabled: !!keyword,
  });

export const useLoginMutation = () =>
  useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      login(email, password),
  });

export const useFetchCurrentUserQuery = (enabled = true) =>
  useQuery({
    queryKey: ['user', 'current'],
    queryFn: () => fetchUser(),
    enabled,
    staleTime: 30_000,
  });
